/* ────────────────────────────────────────────────────────────────────────────
   Regional crop sets for the CropChipPicker quick-pick grid.

   The signup + onboarding flows both collect a location BEFORE the crop step
   (signup: free-text "Farm location"; onboarding: mailing state + ZIP). We parse
   a US state (2-letter code OR full name) and, for climate-spanning states
   (FL/TX), a ZIP, then map to a broad growing region and lead the tiles with
   crops appropriate to it. Fuzzy input (a bare city, no state) → the default
   (tropical) set — the flagship South-Florida market. No backend / no network:
   this is an instant client-side reorder. The USDA plant-hardiness zone is the
   "correct" signal but needs a ~40k-row ZIP→zone table; state-level (ZIP-refined
   for FL/TX) is plenty for ordering tiles. A Census-geocode upgrade for lat-based
   precision is an easy later follow-up.
──────────────────────────────────────────────────────────────────────────── */

export type TopCrop = { name: string; emoji: string };

export type RegionKey = "tropical" | "citrus" | "desert" | "mediterranean" | "temperate" | "southeast";

// Tropical / South-Florida-first — also the DEFAULT (flagship market, and the
// safe fallback for unknown locations). Kept identical to the prior default set.
export const DEFAULT_TOP_CROPS: TopCrop[] = [
  { name: "Mango", emoji: "🥭" },
  { name: "Avocado", emoji: "🥑" },
  { name: "Citrus", emoji: "🍊" },
  { name: "Lemon / Lime", emoji: "🍋" },
  { name: "Banana", emoji: "🍌" },
  { name: "Lychee", emoji: "🍒" },
  { name: "Longan", emoji: "🟤" },
  { name: "Papaya", emoji: "🍈" },
  { name: "Guava", emoji: "🍐" },
  { name: "Jackfruit", emoji: "🌳" },
  { name: "Starfruit", emoji: "⭐" },
  { name: "Dragonfruit", emoji: "🐉" },
  { name: "Passionfruit", emoji: "🟣" },
  { name: "Pineapple", emoji: "🍍" },
  { name: "Coconut", emoji: "🥥" },
  { name: "Fig", emoji: "🫐" },
  { name: "Sugar apple", emoji: "🍏" },
  { name: "Soursop", emoji: "🟢" },
  { name: "Sapodilla", emoji: "🟤" },
  { name: "Mamey", emoji: "🟠" },
  { name: "Strawberry", emoji: "🍓" },
  { name: "Herbs", emoji: "🌿" },
];

// Humid subtropical citrus belt — Central/North Florida + the Texas Rio Grande Valley.
// (Arizona low desert split out to DESERT — dates/olives rot/are marginal in humid FL.)
const CITRUS: TopCrop[] = [
  { name: "Citrus", emoji: "🍊" },
  { name: "Lemon", emoji: "🍋" },
  { name: "Lime", emoji: "🟢" },
  { name: "Grapefruit", emoji: "🍊" },
  { name: "Satsuma", emoji: "🍊" },
  { name: "Mango", emoji: "🥭" },
  { name: "Avocado", emoji: "🥑" },
  { name: "Loquat", emoji: "🟡" },
  { name: "Kumquat", emoji: "🟠" },
  { name: "Guava", emoji: "🍐" },
  { name: "Blueberry", emoji: "🫐" },
  { name: "Blackberry", emoji: "⚫" },
  { name: "Peach", emoji: "🍑" },
  { name: "Fig", emoji: "🫐" },
  { name: "Persimmon", emoji: "🟠" },
  { name: "Muscadine", emoji: "🍇" },
  { name: "Papaya", emoji: "🍈" },
  { name: "Pecan", emoji: "🌰" },
  { name: "Strawberry", emoji: "🍓" },
  { name: "Herbs", emoji: "🌿" },
];

// Arid / low desert — Arizona (Phoenix/Yuma) + Coachella-style desert valleys.
// Dates, citrus, olives, nuts, low-chill stone fruit.
const DESERT: TopCrop[] = [
  { name: "Citrus", emoji: "🍊" },
  { name: "Date", emoji: "🟤" },
  { name: "Grapefruit", emoji: "🍊" },
  { name: "Lemon", emoji: "🍋" },
  { name: "Olive", emoji: "🫒" },
  { name: "Pomegranate", emoji: "🔴" },
  { name: "Fig", emoji: "🫐" },
  { name: "Pistachio", emoji: "🌰" },
  { name: "Almond", emoji: "🌰" },
  { name: "Pecan", emoji: "🌰" },
  { name: "Apricot", emoji: "🟠" },
  { name: "Peach", emoji: "🍑" },
  { name: "Persimmon", emoji: "🟠" },
  { name: "Jujube", emoji: "🟤" },
  { name: "Prickly pear", emoji: "🌵" },
  { name: "Quince", emoji: "🟡" },
  { name: "Grapes", emoji: "🍇" },
  { name: "Guava", emoji: "🍐" },
  { name: "Kumquat", emoji: "🟠" },
  { name: "Herbs", emoji: "🌿" },
];

// Mediterranean / dry-summer — California.
const MEDITERRANEAN: TopCrop[] = [
  { name: "Grapes", emoji: "🍇" },
  { name: "Citrus", emoji: "🍊" },
  { name: "Lemon", emoji: "🍋" },
  { name: "Avocado", emoji: "🥑" },
  { name: "Olive", emoji: "🫒" },
  { name: "Fig", emoji: "🫐" },
  { name: "Almond", emoji: "🌰" },
  { name: "Walnut", emoji: "🌰" },
  { name: "Pomegranate", emoji: "🔴" },
  { name: "Persimmon", emoji: "🟠" },
  { name: "Peach", emoji: "🍑" },
  { name: "Plum", emoji: "🟣" },
  { name: "Apricot", emoji: "🟠" },
  { name: "Cherry", emoji: "🍒" },
  { name: "Date", emoji: "🟤" },
  { name: "Kiwi", emoji: "🥝" },
  { name: "Pistachio", emoji: "🌰" },
  { name: "Strawberry", emoji: "🍓" },
  { name: "Apple", emoji: "🍎" },
  { name: "Herbs", emoji: "🌿" },
];

// Temperate — Northeast, Midwest, PNW, Mountain, Northern tier. Apple leads.
const TEMPERATE: TopCrop[] = [
  { name: "Apple", emoji: "🍎" },
  { name: "Cherry", emoji: "🍒" },
  { name: "Peach", emoji: "🍑" },
  { name: "Pear", emoji: "🍐" },
  { name: "Plum", emoji: "🟣" },
  { name: "Apricot", emoji: "🟠" },
  { name: "Blueberry", emoji: "🫐" },
  { name: "Strawberry", emoji: "🍓" },
  { name: "Raspberry", emoji: "🔴" },
  { name: "Blackberry", emoji: "⚫" },
  { name: "Grapes", emoji: "🍇" },
  { name: "Cranberry", emoji: "🔴" },
  { name: "Currant", emoji: "🟣" },
  { name: "Rhubarb", emoji: "🌿" },
  { name: "Elderberry", emoji: "🫐" },
  { name: "Hazelnut", emoji: "🌰" },
  { name: "Walnut", emoji: "🌰" },
  { name: "Quince", emoji: "🟡" },
  { name: "Fig", emoji: "🫐" },
  { name: "Herbs", emoji: "🌿" },
];

// Southeast / humid subtropical — GA, Carolinas, AL, MS, TN, AR, LA, VA, OK, E-TX. Peach leads.
const SOUTHEAST: TopCrop[] = [
  { name: "Peach", emoji: "🍑" },
  { name: "Pecan", emoji: "🌰" },
  { name: "Blueberry", emoji: "🫐" },
  { name: "Muscadine", emoji: "🍇" },
  { name: "Apple", emoji: "🍎" },
  { name: "Strawberry", emoji: "🍓" },
  { name: "Fig", emoji: "🫐" },
  { name: "Blackberry", emoji: "⚫" },
  { name: "Watermelon", emoji: "🍉" },
  { name: "Persimmon", emoji: "🟠" },
  { name: "Pear", emoji: "🍐" },
  { name: "Plum", emoji: "🟣" },
  { name: "Satsuma", emoji: "🍊" },
  { name: "Pomegranate", emoji: "🔴" },
  { name: "Kiwi", emoji: "🥝" },
  { name: "Chestnut", emoji: "🌰" },
  { name: "Pawpaw", emoji: "🟢" },
  { name: "Raspberry", emoji: "🔴" },
  { name: "Sweet corn", emoji: "🌽" },
  { name: "Herbs", emoji: "🌿" },
];

const REGION_CROPS: Record<RegionKey, TopCrop[]> = {
  tropical: DEFAULT_TOP_CROPS,
  citrus: CITRUS,
  desert: DESERT,
  mediterranean: MEDITERRANEAN,
  temperate: TEMPERATE,
  southeast: SOUTHEAST,
};

// Master emoji lookup (union of every region set) + a few aliases, so a
// searched/custom crop chip still gets a sensible glyph regardless of region.
const CROP_EMOJI: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  for (const set of Object.values(REGION_CROPS)) {
    for (const c of set) m[c.name.toLowerCase()] = c.emoji;
  }
  return m;
})();
const EMOJI_ALIASES: Record<string, string> = {
  orange: "🍊", tangerine: "🍊", mandarin: "🍊", grapefruit: "🍊",
  lime: "🟢", "lemon / lime": "🍋",
  atemoya: "🍏", "sugar apple": "🍏", soursop: "🟢", cherimoya: "🍏",
  carambola: "⭐", mamey: "🟠", sapodilla: "🟤", canistel: "🟡",
  nectarine: "🍑", prune: "🟣", almond: "🌰", pistachio: "🌰", pecan: "🌰",
  grape: "🍇", vine: "🍇", muscadine: "🍇", scuppernong: "🍇",
  satsuma: "🍊", tangelo: "🍊", clementine: "🍊", loquat: "🟡", kumquat: "🟠",
  "prickly pear": "🌵", jujube: "🟤", quince: "🟡", pawpaw: "🟢",
  herb: "🌿", mint: "🌿", basil: "🌿", rosemary: "🌿",
};

export function emojiForCrop(name: string): string {
  const k = name.trim().toLowerCase();
  return CROP_EMOJI[k] ?? EMOJI_ALIASES[k] ?? "🌱";
}

// ── Location → region ───────────────────────────────────────────────────────

const STATE_REGION: Record<string, RegionKey> = {
  AL: "southeast", AK: "temperate", AZ: "desert", AR: "southeast", CA: "mediterranean",
  CO: "temperate", CT: "temperate", DE: "temperate", DC: "temperate", GA: "southeast",
  HI: "tropical", ID: "temperate", IL: "temperate", IN: "temperate", IA: "temperate",
  KS: "temperate", KY: "southeast", LA: "southeast", ME: "temperate", MD: "temperate",
  MA: "temperate", MI: "temperate", MN: "temperate", MS: "southeast", MO: "temperate",
  MT: "temperate", NE: "temperate", NV: "temperate", NH: "temperate", NJ: "temperate",
  NM: "temperate", NY: "temperate", NC: "southeast", ND: "temperate", OH: "temperate",
  OK: "southeast", OR: "temperate", PA: "temperate", RI: "temperate", SC: "southeast",
  SD: "temperate", TN: "southeast", UT: "temperate", VT: "temperate", VA: "southeast",
  WA: "temperate", WV: "temperate", WI: "temperate", WY: "temperate",
  PR: "tropical", GU: "tropical", VI: "tropical", AS: "tropical", MP: "tropical",
  // FL + TX are ZIP-refined below (span multiple climates).
};

const STATE_NAMES: Record<string, string> = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR", california: "CA",
  colorado: "CO", connecticut: "CT", delaware: "DE", florida: "FL", georgia: "GA",
  hawaii: "HI", idaho: "ID", illinois: "IL", indiana: "IN", iowa: "IA", kansas: "KS",
  kentucky: "KY", louisiana: "LA", maine: "ME", maryland: "MD", massachusetts: "MA",
  michigan: "MI", minnesota: "MN", mississippi: "MS", missouri: "MO", montana: "MT",
  nebraska: "NE", nevada: "NV", "new hampshire": "NH", "new jersey": "NJ",
  "new mexico": "NM", "new york": "NY", "north carolina": "NC", "north dakota": "ND",
  ohio: "OH", oklahoma: "OK", oregon: "OR", pennsylvania: "PA", "rhode island": "RI",
  "south carolina": "SC", "south dakota": "SD", tennessee: "TN", texas: "TX", utah: "UT",
  vermont: "VT", virginia: "VA", washington: "WA", "west virginia": "WV", wisconsin: "WI",
  wyoming: "WY", "puerto rico": "PR", guam: "GU", "district of columbia": "DC",
};

const STATE_CODES = new Set([...Object.keys(STATE_REGION), "FL", "TX"]);

// State names checked longest-first, so "west virginia" wins over "virginia".
const STATE_NAMES_BY_LEN = Object.keys(STATE_NAMES).sort((a, b) => b.length - a.length);

// Resolve a US state from free text (e.g. "Cape Coral, FL 33991" or "Traverse
// City, Michigan"). Full state NAMES are matched first — they're unambiguous;
// a bare 2-letter code can collide with an English word ("IN", "OR"), so it's
// the fallback, and we take the LAST such token (usually the trailing state).
function stateCodeIn(loc: string): string | null {
  const lower = loc.toLowerCase();
  for (const name of STATE_NAMES_BY_LEN) {
    if (new RegExp(`\\b${name.replace(/ /g, "\\s+")}\\b`).test(lower)) return STATE_NAMES[name];
  }
  const codes = loc.toUpperCase().match(/\b[A-Z]{2}\b/g);
  if (codes) {
    for (let i = codes.length - 1; i >= 0; i--) if (STATE_CODES.has(codes[i])) return codes[i];
  }
  return null;
}

function zipIn(loc: string): string | null {
  const m = loc.match(/\b(\d{5})(?:-\d{4})?\b/);
  return m ? m[1] : null;
}

// South-and-SW Florida ZIP3 prefixes where true tropical fruit (mango/lychee)
// thrives (Miami-Dade/Broward/Palm Beach/Lee/Collier); the rest of FL is citrus/
// subtropical. Rio Grande Valley (TX 785xx) is TX citrus country.
const FL_TROPICAL_ZIP3 = new Set(["330", "331", "332", "333", "334", "339", "341"]);

export function regionFromLocation(loc?: string | null): RegionKey {
  if (!loc || !loc.trim()) return "tropical";
  const state = stateCodeIn(loc);
  const zip3 = (zipIn(loc) ?? "").slice(0, 3);
  if (state === "FL") return zip3 && !FL_TROPICAL_ZIP3.has(zip3) ? "citrus" : "tropical";
  if (state === "TX") return zip3 === "785" ? "citrus" : "southeast";
  if (state && STATE_REGION[state]) return STATE_REGION[state];
  return "tropical";
}

export function cropsForLocation(loc?: string | null): TopCrop[] {
  return REGION_CROPS[regionFromLocation(loc)];
}
